import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchUserArea, updateUserArea, createUserArea, deleteUserArea } from "Redux/Admin/HabitManager/UserAreas/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IUserArea } from 'Redux/Admin/HabitManager/UserAreas/type';

const SLUG = "hm-user-areas"
const TITLE = "User Area";

const UserArea = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchHmUserAreas);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchUserArea(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createUserArea(values, handleCreate));
    }
    else if (id) {
      dispatch(updateUserArea(values, id));
    }
  };

  const handleCreate = (project: IUserArea) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/habit-manager/${SLUG}/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/habit-manager/${SLUG}`, { replace: true });
    return null;
  }

  const userarea = isNew ? initialValues : entities[id];

  if (!userarea) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: `Delete ${TITLE}`,
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: `Delete the ${TITLE}?`,
    content: <span>Are you sure you want to delete the {TITLE} <b>{userarea.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteUserArea(id, () => {
          navigate(`${ADMIN_PATH}/habit-manager/${SLUG}`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/habit-manager/${SLUG}`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } ${TITLE}`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage {TITLE}
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={userarea}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default UserArea;
